import { useCallback, useEffect, useRef, useState } from 'react'

// Long enough not to flash a card at every pin you sweep past, short enough that
// holding still feels like the card is answering rather than loading. 1200ms was
// past that line: the open timer bails when the pointer has left the pin, so any
// drift inside the hold — easy on a small pin in a cluster — produced no card at
// all, and the same map shows container cards with no delay, which made stop pins
// read as broken.
const OPEN_DELAY_MS = 300
// Kept long: this one covers the trip from the pin to the card.
const CLOSE_DELAY_MS = 1000

const useBulkMarkerPopover = ({ stopsById }) => {
  const [popoverState, setPopoverState] = useState({ stop: null, position: null })
  const hoverRef = useRef(false)
  const popoverHoveredRef = useRef(false)
  const openTimerRef = useRef(null)
  const closeTimerRef = useRef(null)
  const pendingStopIdRef = useRef(null)
  const latestPositionRef = useRef(null)
  const currentStopIdRef = useRef(null)
  const isBoxSelectingRef = useRef(false)

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Shift') {
        isBoxSelectingRef.current = true
        hoverRef.current = false
        popoverHoveredRef.current = false
        if (openTimerRef.current) {
          clearTimeout(openTimerRef.current)
          openTimerRef.current = null
        }
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current)
          closeTimerRef.current = null
        }
        pendingStopIdRef.current = null
        currentStopIdRef.current = null
        setPopoverState({ stop: null, position: null })
      }
    }
    const onKeyUp = e => {
      if (e.key === 'Shift') {
        isBoxSelectingRef.current = false
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      if (openTimerRef.current) clearTimeout(openTimerRef.current)
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  const handleMarkerMouseEnter = useCallback(
    (latLng, stopId) => {
      if (isBoxSelectingRef.current) return
      hoverRef.current = true
      latestPositionRef.current = latLng

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }

      if (currentStopIdRef.current === stopId) return

      if (pendingStopIdRef.current === stopId) return

      if (openTimerRef.current) clearTimeout(openTimerRef.current)
      pendingStopIdRef.current = stopId

      openTimerRef.current = setTimeout(() => {
        if (!hoverRef.current) return
        pendingStopIdRef.current = null
        const stop = stopsById[stopId]
        if (stop) {
          currentStopIdRef.current = stopId
          setPopoverState({ stop, position: latestPositionRef.current })
        }
      }, OPEN_DELAY_MS)
    },
    [stopsById]
  )

  const handleMarkerMouseLeave = useCallback(() => {
    hoverRef.current = false
    pendingStopIdRef.current = null

    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }

    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      if (!hoverRef.current && !popoverHoveredRef.current) {
        currentStopIdRef.current = null
        setPopoverState({ stop: null, position: null })
      }
    }, CLOSE_DELAY_MS)
  }, [])

  const handlePopoverMouseEnter = useCallback(() => {
    popoverHoveredRef.current = true
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const handlePopoverMouseLeave = useCallback(() => {
    popoverHoveredRef.current = false
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      if (!hoverRef.current && !popoverHoveredRef.current) {
        currentStopIdRef.current = null
        setPopoverState({ stop: null, position: null })
      }
    }, CLOSE_DELAY_MS)
  }, [])

  return {
    popoverStop: popoverState.stop,
    popoverPosition: popoverState.position,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
  }
}

export default useBulkMarkerPopover
